'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useTheme } from 'styled-components';
import { addAnalyticsDataAttributes } from '../../lib/analytics';
import { Carat, Minus, Plus, RotateClockwise } from '../Icons';
import { AccordionContext, AccordionSectionContext, AccordionSingleOpenContext, useAccordionContext, useAccordionSectionContext, useAccordionSingleOpenContext, } from './context/Accordion';
import { StyledAccordionBodyContainer, StyledAccordionHeader, StyledAccordionHeaderContainer, StyledAccordionHeaderControl, StyledAccordionHeaderIcon, StyledAccordionList, StyledAccordionListItem, } from './styles';
const AccordionSection = ({ index, isActive, disabled, children, }) => {
    const [AccordionSectionIsActive, setAccordionSectionIsActive] = React.useState(isActive);
    const noOp = () => { };
    return (_jsx(AccordionSectionContext.Provider, { value: {
            index,
            disabled,
            isActive: AccordionSectionIsActive,
            setIsActive: disabled ? noOp : setAccordionSectionIsActive,
        }, children: children }));
};
const AccordionHeaderIcon = () => {
    const { icon, variant, multiOpen } = useAccordionContext();
    const { index, disabled, isActive } = useAccordionSectionContext();
    const { currentAccordionSection } = useAccordionSingleOpenContext();
    const theme = useTheme();
    const color = variant === 'dark' ? 'white' : theme.color.button.primary.fill.on.background;
    const currentActive = multiOpen ? isActive : index === currentAccordionSection;
    const plus = currentActive ? _jsx(Minus, { stroke: color }) : _jsx(Plus, { stroke: color });
    const carat = (_jsx(Carat, { stroke: color, rotateClockwise: currentActive ? RotateClockwise.Quarter : undefined }));
    const icons = { plus, carat };
    if (disabled)
        return null;
    return _jsx(StyledAccordionHeaderIcon, { children: icons[icon] });
};
const AccordionHeader = ({ children, className }) => {
    const { variant, showBorder, multiOpen, isTransparent, withAnimation } = useAccordionContext();
    const { index, isActive, setIsActive } = useAccordionSectionContext();
    const { currentAccordionSection, setCurrentAccordionSection } = useAccordionSingleOpenContext();
    const currentActive = multiOpen ? isActive : index === currentAccordionSection;
    const handleClick = () => {
        multiOpen
            ? setIsActive(!isActive)
            : setCurrentAccordionSection(currentAccordionSection !== index ? index : -1);
    };
    return (_jsxs(StyledAccordionHeaderContainer, { "$showBorder": showBorder, "$isTransparent": isTransparent, "$variant": variant, "aria-expanded": currentActive, "$currentActive": !!currentActive, onClick: handleClick, "$index": index, className: className, ...addAnalyticsDataAttributes({
            category: 'ui-accordion',
            action: currentActive ? 'collapse' : 'expand',
            label: `content-header-${index}`,
        }), "$withAnimation": withAnimation, type: 'button', children: [_jsx(StyledAccordionHeader, { children: children }), _jsx(StyledAccordionHeaderControl, { children: _jsx(AccordionHeaderIcon, {}) })] }));
};
const AccordionBody = ({ children, className }) => {
    const { variant, showBorder, multiOpen, isTransparent, withAnimation } = useAccordionContext();
    const { index, disabled, isActive } = useAccordionSectionContext();
    const { currentAccordionSection } = useAccordionSingleOpenContext();
    const currentActive = multiOpen ? isActive : index === currentAccordionSection;
    return (_jsx(StyledAccordionBodyContainer, { "$currentActive": currentActive, "$isTransparent": isTransparent, "$showBorder": showBorder, "$variant": variant, "$withAnimation": withAnimation, className: className, id: `content-${index}`, "aria-hidden": !currentActive, children: !disabled && children }));
};
const Accordion = ({ icon, multiOpen, variant, ariaLabel, showBorder, isTransparent, children, withAnimation, ...rest }) => {
    const childrenArray = React.Children.toArray(children);
    const defaultIsActiveAccordion = childrenArray.find((child) => child.props.isActive);
    const [currentAccordionSection, setCurrentAccordionSection] = React.useState(defaultIsActiveAccordion ? defaultIsActiveAccordion.props.index : -1);
    return (_jsx(AccordionContext.Provider, { value: { icon, variant, ariaLabel, showBorder, isTransparent, multiOpen, withAnimation }, children: _jsx(StyledAccordionList, { "$variant": variant, "$isTransparent": isTransparent, ...rest, "aria-label": `Accordion - ${ariaLabel}`, children: childrenArray.map((child, id) => (_jsx(AccordionSingleOpenContext.Provider, { value: { currentAccordionSection, setCurrentAccordionSection }, children: _jsx(StyledAccordionListItem, { children: child }, id) }, id))) }) }));
};
Accordion.Section = AccordionSection;
Accordion.Header = AccordionHeader;
Accordion.Body = AccordionBody;
export { Accordion, AccordionSection, AccordionHeader, AccordionBody };
//# sourceMappingURL=accordion.js.map